import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { SocietyService } from '@/services/societyService';
import { AiService } from '@/services/aiService';

export const ELIGIBLE_AI_ROLES = [
  'PRESIDENT',
  'VICE_PRESIDENT',
  'GENERAL_SECRETARY',
  'JOINT_SECRETARY',
  'TREASURER',
  'EXECUTIVE_MEMBER',
  'COMMITTEE_MEMBER',
  'EXECUTIVE',
];

export interface AiAssistantEligibility {
  isEligibleRole: boolean;
  isEntitled: boolean;
  isAvailable: boolean;
  isLoading: boolean;
}

/**
 * Hook verifying whether the current authenticated user and society
 * can access the Society AI Assistant.
 *
 * Rules:
 * 1. Must NOT be SUPER_ADMIN or MARKETING_PERSON
 * 2. Must be bound to an active Society tenant
 * 3. Role must be in ELIGIBLE_AI_ROLES
 * 4. Society must possess effective AI_ASSISTANT feature entitlement
 */
export function useAiAssistantEligibility(): AiAssistantEligibility {
  const user = useAuthStore((s) => s.user);
  const tenant = useAuthStore((s) => s.tenant);
  const isSuperAdmin = useAuthStore((s) => s.isSuperAdmin());

  const userRoles = useMemo(() => {
    const roles: string[] = [];
    if (user?.roles && Array.isArray(user.roles)) {
      roles.push(...user.roles);
    }
    if ((user as any)?.role) {
      roles.push((user as any).role);
    }
    return roles.map((r) => String(r).toUpperCase());
  }, [user]);

  // 1. Role Eligibility Check
  const isEligibleRole = useMemo(() => {
    if (!user || isSuperAdmin) return false;
    if (userRoles.includes('SUPER_ADMIN') || userRoles.includes('MARKETING_PERSON') || userRoles.includes('MARKETING_AGENT')) {
      return false;
    }
    if (!user.tenantId && !tenant?._id && !(user as any)?.societyId) {
      return false;
    }
    return userRoles.some((r) => ELIGIBLE_AI_ROLES.includes(r));
  }, [user, isSuperAdmin, userRoles, tenant]);

  // 2. Society Feature Entitlement & AI Enabled Check (only executed if role is eligible)
  const entitlementQuery = useQuery({
    queryKey: ['society', 'ai-status', tenant?._id || user?.tenantId],
    queryFn: async () => {
      try {
        // First verify license entitlement
        const res = await SocietyService.getMyEntitlements();
        if (res.data?.features) {
          const aiFeature = res.data.features['AI_ASSISTANT'];
          if (aiFeature && !aiFeature.isEntitled) {
            return false;
          }
        }
        // Second verify executive toggle in AI Config
        const configRes = await AiService.getConfig();
        const cfgData: any = configRes.data || configRes;
        if (cfgData && typeof cfgData.isEnabled === 'boolean') {
          return Boolean(cfgData.isEnabled);
        }
        if (cfgData && typeof cfgData.enabled === 'boolean') {
          return Boolean(cfgData.enabled);
        }
        return false;
      } catch {
        return false;
      }
    },
    enabled: isEligibleRole,
    staleTime: 30 * 1000,
    retry: false,
  });

  const isEntitled = isEligibleRole && entitlementQuery.data === true;
  const isAvailable = isEligibleRole && entitlementQuery.data === true;

  return {
    isEligibleRole,
    isEntitled,
    isAvailable,
    isLoading: isEligibleRole && entitlementQuery.isLoading,
  };
}
